﻿// Per-entity field registry — the single source of truth for WHAT is
// filterable in Stewardship OS (Saved-Filter Builder program, PR 1).
//
// CONSUMER: shared (Workers runtime + web via @shared + tests).
//
// Four consumers read this registry so they can never drift:
//   1. The list endpoints' SQL builder (which WHERE fragment a field
//      compiles to — wired in PR 3/4).
//   2. The "+ Filter" builder UI (field picker groups, operators,
//      value editors — PR 5).
//   3. The full-result export's column/filter allowlist (PR 7).
//   4. The natural-language path's prompt catalog + strict validation
//      (PR 8) — the model picks from THIS list, never invents fields.
//
// Hard rule (mirrors shared/automations/rule-catalog.ts): PURE and
// dependency-free. No React, no DB client.
//
// SENSITIVE-FIELD RULE: nothing encrypted and nothing derived from an
// encrypted value appears here — including `*_last_four` columns.
// Making last-four filterable would create exactly the value-lookup
// side channel the SENSITIVE_FIELD architecture forbids (no index, no
// queryability on those columns). Free-text notes are also excluded
// from v1: filters stay on structured fields.
//
// `wired`: true means the live list endpoint understands this field
// today (the legacy key path). Fields land in the registry first with
// wired:false, then PR 3/4 implement their SQL and flip the flag. The
// builder UI and the NL prompt only expose wired fields, so the
// registry can always describe the full target vocabulary without
// promising filters the server can't run yet.

import type { FilterCondition, FilterFieldType, FilterOp } from "./filter-model";
import { REFERRAL_SOURCE_OPTIONS } from "../contacts/referral-sources";

// Local company union — mirrors rule-catalog.ts's local copy so this
// module stays dependency-free. Client-facing five only (Productions
// is a support entity and never appears in filter vocabulary).
export type FilterCompanySlug =
  | "insurance"
  | "mortgage"
  | "planning"
  | "realty"
  | "financial";

export type FilterEntity =
  | "contacts"
  | "households"
  | "opportunities"
  | "processing"
  | "ongoing-care"
  | "service-records";

export const FILTER_ENTITIES: readonly FilterEntity[] = [
  "contacts",
  "households",
  "opportunities",
  "processing",
  "ongoing-care",
  "service-records",
];

/**
 * Dynamic option sources. The builder UI (PR 5) loads real rows for
 * these from existing endpoints (advisors, stage templates, carriers,
 * …); the registry only names the source. "contact" is a typeahead
 * lookup (referred-by), not a dropdown.
 */
export type FilterOptionsSource =
  | "company"
  | "advisor"
  | "stage"
  | "workflow_type"
  | "rhythm_template"
  | "carrier"
  | "service_type"
  | "service_family"
  | "contact";

export interface FilterFieldOption {
  value: string;
  label: string;
}

export interface FilterField {
  /** Canonical field key — stable wire/API identifier. */
  key: string;
  /** Plain-English label (vocabulary-locked; tested). */
  label: string;
  /** Field-picker group header, e.g. "Person", "Relationship". */
  group: string;
  type: FilterFieldType;
  /** Allowed operators; the FIRST is the builder's default. */
  ops: FilterOp[];
  /** Static choices for enum fields without a dynamic source. */
  options?: FilterFieldOption[];
  /** Dynamic source for enum/string fields (real rows at UI time). */
  optionsSource?: FilterOptionsSource;
  unit?: "days" | "dollars" | "years" | "count" | "score";
  /** Help-on-tap copy. Required: plain words, help on tap is doctrine. */
  help: string;
  /** Example operand(s) for placeholders + the NL prompt. */
  example?: string;
  /** Category-1 (auto-computed) field — derived, never typed in. */
  computed?: boolean;
  /** The live endpoint evaluates this field today (see header). */
  wired: boolean;
  /** Field can drive ORDER BY (PR 3/4 wire sort for these). */
  sortable?: boolean;
  /**
   * The pre-builder `f=key:value` wire key this field absorbs, when
   * it's a simple 1:1 rename. Multi-key legacies (closes_before/
   * closes_after) live in LEGACY_FILTER_MAP instead.
   */
  legacyKey?: string;
  /**
   * Which company's transaction produced the value, what kind of data it
   * is, and how a campaign may use it (Marketing Hub 5.2). Required on
   * every field; the registry test refuses an untagged one.
   */
  dataRights: DataRightsDescriptor;
}

// ─── Data rights (Marketing Hub slice 1, PR 1.4) ─────────────────────
//
// docs/design/marketing-hub.md section 5.2: every field a segment can
// read carries a data-rights descriptor, so the campaign builder can say
// which fields a company may select on and why. NOTHING ENFORCES THEM IN
// THE FILTER COMPILER YET (audit T01, 2026-09-29): PR 1.6 shipped the
// relevance rules, whose rule 5 reads the tag on every fact a candidate
// carries; turning the tags on for segment conditions is the slice-2
// audience builder's job. Until then they are the list Grant approved
// (docs/design/marketing-hub-data-rights-tags.md), kept in step by a test
// and pinned to his approval by a fingerprint.
//
// The plan's defaults, applied here: identity and zero-party fields are
// usable by any company; a company's own transaction and experience
// fields, and application data learned in one company's transaction, are
// usable by that company only (the affiliate rule at audience build
// decides the cross-company case per person); anything inferred, modeled
// or drawn from public record may order a list but never select it;
// health never participates.

export type DataRightsKind =
  | "identity"
  | "transaction_experience"
  | "other_affiliate"
  | "health"
  | "zero_party"
  | "public_record"
  | "inferred"
  | "vendor";

export type DataRightsMarketingUse =
  | "any"
  | "own_company_only"
  | "ordering_only"
  | "never";

/**
 * Whose transaction produced the value.
 *   null             household identity or zero-party data; no company owns it
 *   a company slug   one named company's transaction (a fixed column)
 *   "record_company" the company on the row itself (a workflow or service
 *                    record's own company_id), resolved per row
 */
export type DataRightsSourceCompany = FilterCompanySlug | "record_company" | null;

export interface DataRightsDescriptor {
  sourceCompany: DataRightsSourceCompany;
  kind: DataRightsKind;
  marketingUse: DataRightsMarketingUse;
}

/** The handful of shapes the 90 fields fall into. Named so a reviewer
 *  can read a field's tag as a phrase. */
export const DATA_RIGHTS = {
  /** Who the person is: age, city, consent flags. Any company. */
  identity: { sourceCompany: null, kind: "identity", marketingUse: "any" },
  /** What the person told us about themselves or their origin. Any company. */
  zeroParty: { sourceCompany: null, kind: "zero_party", marketingUse: "any" },
  /** The relationship layer itself: which companies serve the family, since
   *  when, how recently we spoke. Stewardship's own experience, held at the
   *  household grain by every company; the per-person affiliate rule at
   *  audience build governs cross-company solicitation, not this tag. */
  relationship: { sourceCompany: null, kind: "transaction_experience", marketingUse: "any" },
  /** An advisor's recorded judgment about the family (fit, stage of wealth).
   *  Internal-only, and by standing doctrine (Grant, 2026-07-03) fit stays an
   *  opt-in audience filter, so this is NOT ordering-only. On the review list. */
  advisorJudgment: { sourceCompany: null, kind: "inferred", marketingUse: "any" },
  /** The record's own company's fit assessment, on an Opportunity or
   *  Processing list: that company's judgment, so that company's to use
   *  (audit T03, 2026-09-29; it was tagged as no company's). */
  recordJudgment: { sourceCompany: "record_company", kind: "inferred", marketingUse: "own_company_only" },
  /** How the family came to us, as the team recorded it: a channel, a
   *  partner, a handoff or a system stamp, rarely the family's own words,
   *  so Stewardship's relationship data, not zero-party (audit T02). */
  originRecorded: { sourceCompany: null, kind: "transaction_experience", marketingUse: "any" },
  /** A score the OS computed about the relationship (engagement tiers).
   *  Derived, so it may order a list but never select one. */
  computedScore: { sourceCompany: null, kind: "inferred", marketingUse: "ordering_only" },
  /** Insurance's own transaction and experience (policies held, premium, claims). */
  insuranceTransaction: { sourceCompany: "insurance", kind: "transaction_experience", marketingUse: "own_company_only" },
  /** Application data learned in an Insurance transaction (driver discounts). */
  insuranceApplication: { sourceCompany: "insurance", kind: "other_affiliate", marketingUse: "own_company_only" },
  /** Financial's own transaction and experience (AUM). */
  financialTransaction: { sourceCompany: "financial", kind: "transaction_experience", marketingUse: "own_company_only" },
  /** The record's own company's transaction and experience (stage, owner,
   *  dates, our revenue). Resolved per row. */
  recordTransaction: { sourceCompany: "record_company", kind: "transaction_experience", marketingUse: "own_company_only" },
  /** Application data on the record itself (the product metric: origination
   *  amount, premium, AUM as the client stated it). Resolved per row. */
  recordApplication: { sourceCompany: "record_company", kind: "other_affiliate", marketingUse: "own_company_only" },
  /** What the county recorder says about a house behind a Mortgage loan. */
  mortgagePublicRecord: { sourceCompany: "mortgage", kind: "public_record", marketingUse: "ordering_only" },
  /** Row 10.2: a public record in the signal ledger (a recorded sale, a new
   *  loan elsewhere, a second lien, a listing an advisor saw). Ordering-only
   *  for every list and every automated piece; relevance rules 5 and 6 let it
   *  put a family on Today at the two advisor tiers only (S10-10). */
  publicRecord: { sourceCompany: null, kind: "public_record", marketingUse: "ordering_only" },
} as const satisfies Record<string, DataRightsDescriptor>;

// ─── Shared option lists ─────────────────────────────────────────────

export const COMPANY_OPTIONS: FilterFieldOption[] = [
  { value: "mortgage", label: "Mortgage" },
  { value: "insurance", label: "Insurance" },
  { value: "planning", label: "Planning" },
  { value: "realty", label: "Realty" },
  { value: "financial", label: "Financial" },
];

// Marketing opt-out (2026-10-29, Marketing Hub register S6-3). One value per
// company reads the live "stop this company's marketing email" row the send
// paths read; "any" is any such row; "all" is the firm-wide stop
// (contacts.marketing_ok = 0). READ-ONLY: nothing here changes a preference.
export const MARKETING_OPT_OUT_OPTIONS: FilterFieldOption[] = [
  ...COMPANY_OPTIONS,
  { value: "any", label: "Any company" },
  { value: "all", label: "All marketing" },
];

const ANY_COMPANY_OPTIONS: FilterFieldOption[] = [
  { value: "any", label: "Any company" },
  ...COMPANY_OPTIONS,
];

// User-facing fit labels per the vocabulary lock (Jerry/George stay
// internal speech; UI surfaces use the softened labels).
export const FIT_OPTIONS: FilterFieldOption[] = [
  { value: "ideal_fit", label: "Ideal Fit" },
  { value: "typical", label: "Typical Fit" },
  { value: "poor_fit", label: "Not A Fit" },
  { value: "unassessed", label: "Unassessed" },
  // Terminal "can't assess" state (0172): still 'unassessed' in the
  // grade column; the reason column marks it. Filterable so the
  // advisor-departed book is one query away (Jake Test).
  { value: "advisor_departed", label: "Advisor Left (can't assess)" },
];

// Stage of Wealth — Control / Margin / Impact (2026-09-18). ONE per
// family, so unlike fit there is no "best across companies" to resolve.
//
// "not_set" and "cant_say" are filterable on purpose: "who has nobody
// said anything about?" and "who did we look at and genuinely not know?"
// are different questions with different answers, and the second is the
// list that stops being asked again.
export const WEALTH_STAGE_OPTIONS: FilterFieldOption[] = [
  { value: "control", label: "Control" },
  { value: "margin", label: "Margin" },
  { value: "impact", label: "Impact" },
  { value: "not_set", label: "Not set" },
  { value: "cant_say", label: "We can't say yet" },
];

// Relationship-engagement tier (migration 0193). Combined 0-100 score
// bucketed for at-a-glance filtering; the two axes (service effort /
// responsiveness) are separately filterable as numbers.
export const ENGAGEMENT_TIER_OPTIONS: FilterFieldOption[] = [
  { value: "thriving", label: "Thriving" },
  { value: "steady", label: "Steady" },
  { value: "slipping", label: "Slipping" },
  { value: "disengaged", label: "Disengaged" },
];

// Outcomes differ per workflow: opportunities are won/lost/abandoned;
// processing records complete, fall through, or get cancelled (the
// schema CHECKs in 0003). "open" is the sentinel for outcome IS NULL.
const OPPORTUNITY_OUTCOME_OPTIONS: FilterFieldOption[] = [
  { value: "open", label: "Open" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "abandoned", label: "Abandoned" },
];

const PROCESSING_OUTCOME_OPTIONS: FilterFieldOption[] = [
  { value: "open", label: "Open" },
  { value: "completed", label: "Completed" },
  { value: "fell_through", label: "Fell through" },
  { value: "cancelled", label: "Cancelled" },
];

const MONTH_OPTIONS: FilterFieldOption[] = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

// Referral-source options come from the single shared taxonomy
// (shared/contacts/referral-sources.ts) so the filter vocabulary can never
// drift from the capture UIs and the opportunity source column. That module
// carries the {value,label,category} triple; the registry only needs
// {value,label}.
const REFERRAL_SOURCE_FILTER_OPTIONS: FilterFieldOption[] =
  REFERRAL_SOURCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

// service_records.status CHECK values (schema 0003).
const SERVICE_STATUS_OPTIONS: FilterFieldOption[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "replaced", label: "Replaced" },
  { value: "past", label: "Past" },
  { value: "archived", label: "Archived" },
];

// rhythm_instances.status CHECK values (schema 0003); labels follow
// the one-vocabulary rule from audit A5 ("Ended" = stored 'cancelled').
const RHYTHM_STATUS_OPTIONS: FilterFieldOption[] = [
  { value: "active", label: "Running" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Ended" },
];

const BOOLEAN_OPTIONS: FilterFieldOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

// ─── Field definitions per entity ────────────────────────────────────

const CONTACT_FIELDS: FilterField[] = [
  // Person
  {
    key: "age",
    dataRights: DATA_RIGHTS.identity,
    label: "Age",
    group: "Person",
    type: "number",
    ops: ["between", "gte", "lte", "eq"],
    unit: "years",
    help: "Computed from birthday — nobody types ages in. “Between 55 and 65” answers the original Jake question.",
    example: "between 55 and 65",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "birthday_month",
    dataRights: DATA_RIGHTS.identity,
    label: "Birthday month",
    group: "Person",
    type: "enum",
    ops: ["eq"],
    options: MONTH_OPTIONS,
    help: "Everyone whose birthday falls in this month, any year.",
    wired: true,
    legacyKey: "birthday_month",
  },
  // Location
  {
    key: "city",
    dataRights: DATA_RIGHTS.identity,
    label: "City",
    group: "Location",
    type: "string",
    ops: ["eq", "contains"],
    help: "The contact's city (falls back to the household's city).",
    example: "Gilbert",
    wired: true,
    legacyKey: "city",
    sortable: true,
  },
  {
    key: "state",
    dataRights: DATA_RIGHTS.identity,
    label: "State",
    group: "Location",
    type: "string",
    ops: ["eq", "in"],
    help: "Two-letter state code, e.g. AZ.",
    example: "AZ",
    wired: true,
  },
  {
    key: "zip",
    dataRights: DATA_RIGHTS.identity,
    label: "ZIP code",
    group: "Location",
    type: "string",
    ops: ["eq", "contains"],
    help: "Exact ZIP, or “contains” for a prefix like 852.",
    example: "85234",
    wired: true,
  },
  // Relationship
  {
    key: "company",
    dataRights: DATA_RIGHTS.relationship,
    label: "Has relationship with",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: COMPANY_OPTIONS,
    help: "The household has any relationship with this company — prospect, active client, or past client. “Is any of” scopes to your companies at once.",
    wired: true,
    legacyKey: "company",
  },
  {
    key: "active_client_of",
    dataRights: DATA_RIGHTS.relationship,
    label: "Active client of",
    group: "Relationship",
    type: "enum",
    // "Is any of" (2026-08-20) makes an OR across companies expressible:
    // "active client of Insurance or Mortgage". An AND across companies
    // is two conditions — the set already ANDs them — which is how
    // "clients of both Insurance and Mortgage" is built.
    // "Is not" (2026-10-29) is the cross-sell gap: "Mortgage clients who
    // are not Planning clients" is "is Mortgage" AND "is not Planning".
    // Baker asked Ask Stew for exactly that and it could not subtract.
    ops: ["eq", "neq", "in"],
    options: ANY_COMPANY_OPTIONS,
    help: "Active client = the household has at least one active service with that company. Computed from service records, never typed. “Is any of” matches households active with ANY of the companies picked; add a second “Active client of” filter to require BOTH. “Is not” leaves out that company's active clients (pair it with another company to find a cross-sell gap). “Any company” means a current Stewardship household.",
    computed: true,
    wired: true,
  },
  {
    key: "prospect_of",
    dataRights: DATA_RIGHTS.relationship,
    label: "Prospect of",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: ANY_COMPANY_OPTIONS,
    help: "The household is currently in Opportunities with that company. “Is any of” matches any of the companies picked.",
    computed: true,
    wired: true,
  },
  {
    key: "fit",
    dataRights: DATA_RIGHTS.advisorJudgment,
    label: "Fit assessment",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: FIT_OPTIONS,
    help: "The household's fit assessment (best across companies). Internal-only — never shown to households.",
    wired: true,
    legacyKey: "fit",
  },
  {
    key: "wealth_stage",
    dataRights: DATA_RIGHTS.advisorJudgment,
    label: "Stage of Wealth",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: WEALTH_STAGE_OPTIONS,
    help: "Where this family is in Stewardship's three stages of wealth building: Control (getting the basics in place), Margin (room to breathe, starting to invest), Impact (real wealth and a plan for it). One stage per household, recorded by an advisor. Internal-only — never shown to households.",
    example: "is Margin",
    wired: true,
  },
  {
    key: "services_count",
    dataRights: DATA_RIGHTS.relationship,
    label: "Companies served",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte", "eq", "between"],
    unit: "count",
    help: "How many Stewardship companies actively serve this household. “At least 2” finds multi-service households; “is 1” finds cross-serve candidates.",
    example: "is 1",
    computed: true,
    wired: true,
    legacyKey: "services_count_min",
  },
  {
    key: "last_contact_days",
    dataRights: DATA_RIGHTS.relationship,
    label: "Days since last contact",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "days",
    help: "Days since the last real touch — calls, meetings, personal emails. Bulk sends never count.",
    example: "at least 90",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "last_inbound_days",
    dataRights: DATA_RIGHTS.relationship,
    label: "Days since they last engaged",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "days",
    help: "Days since the client last reached back — replied, called or texted in, or kept a meeting. Never-replied counts as maximally stale. Finds people going quiet on us.",
    example: "at least 90",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "last_outbound_days",
    dataRights: DATA_RIGHTS.relationship,
    label: "Days since we last reached out",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "days",
    help: "Days since we last contacted the client — our calls, meetings, and personal emails. Bulk sends never count. Finds people we owe a touch.",
    example: "at least 90",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "engagement_tier",
    dataRights: DATA_RIGHTS.computedScore,
    label: "Engagement",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: ENGAGEMENT_TIER_OPTIONS,
    help: "Overall relationship engagement — Thriving / Steady / Slipping / Disengaged. Combines whether we're keeping cadence with the client and whether they engage back.",
    computed: true,
    wired: true,
  },
  {
    key: "engagement_service_effort",
    dataRights: DATA_RIGHTS.computedScore,
    label: "Service effort (0-100)",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "score",
    help: "Are WE doing our job — outbound cadence vs. what this client's fit tier should get. Low = we owe them a touch.",
    example: "at most 50",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "engagement_responsiveness",
    dataRights: DATA_RIGHTS.computedScore,
    label: "Responsiveness (0-100)",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "score",
    help: "Is the CLIENT engaging back — replies, inbound contact, recent reviews. Low = going quiet on us.",
    example: "at most 50",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "client_since",
    dataRights: DATA_RIGHTS.relationship,
    label: "Client since",
    group: "Relationship",
    type: "date",
    ops: ["lte", "gte", "between", "more_than_days_ago", "within_last_days"],
    help: "When the household first became a client of any Stewardship company.",
    example: "2022-01-01",
    wired: true,
    sortable: true,
  },
  {
    key: "current_advisor",
    dataRights: DATA_RIGHTS.relationship,
    label: "Current advisor",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "advisor",
    help: "The advisor currently responsible for the household in any company.",
    wired: true,
  },
  {
    key: "referral_source_type",
    dataRights: DATA_RIGHTS.originRecorded,
    label: "Referral source",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: REFERRAL_SOURCE_FILTER_OPTIONS,
    help: "Where this contact originally came from.",
    wired: true,
  },
  {
    key: "referred_by",
    dataRights: DATA_RIGHTS.zeroParty,
    label: "Referred by",
    group: "Relationship",
    type: "string",
    ops: ["eq"],
    optionsSource: "contact",
    help: "Everyone referred by one specific person in the database.",
    wired: true,
  },
  // Contact & consent
  {
    key: "marketing_ok",
    dataRights: DATA_RIGHTS.identity,
    label: "Marketing emails allowed",
    group: "Contact & consent",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "Whether the contact is opted in to marketing (bulk) email.",
    wired: true,
  },
  {
    key: "do_not_email",
    dataRights: DATA_RIGHTS.identity,
    label: "Do-not-email override",
    group: "Contact & consent",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "The rare total-stop flag — halts every send, including transactional.",
    wired: true,
  },
  {
    key: "marketing_opt_out",
    dataRights: DATA_RIGHTS.identity,
    label: "Marketing opt-out",
    group: "Contact & consent",
    type: "enum",
    ops: ["eq", "in"],
    options: MARKETING_OPT_OUT_OPTIONS,
    help: "Who asked a company to stop its marketing email. Pick a company to find people who opted out of that company (the same record every send checks), “Any company” for anyone who opted out of at least one, or “All marketing” for people who stopped all five. “Is any of” matches any of the picks. Read-only: this finds people, it never changes a preference.",
    computed: true,
    wired: true,
  },
  // Driver discounts (migration 0226, Joy 2026-07-30). Grouped on their
  // own so the auto-quoting questions sit together in the filter picker.
  {
    key: "good_student",
    dataRights: DATA_RIGHTS.insuranceApplication,
    label: "Good Student discount",
    group: "Driver discounts",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "Driver meets the carrier's academic threshold (typically a B average).",
    wired: true,
  },
  {
    key: "driver_training",
    dataRights: DATA_RIGHTS.insuranceApplication,
    label: "Driver Training discount",
    group: "Driver discounts",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "Driver has completed an approved driver training course.",
    wired: true,
  },
  // Record
  {
    key: "archived",
    dataRights: DATA_RIGHTS.identity,
    label: "Archived",
    group: "Record",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "“Yes” shows only archived contacts (the list hides them by default).",
    wired: true,
    legacyKey: "archived",
  },
];

const HOUSEHOLD_FIELDS: FilterField[] = [
  {
    key: "company",
    dataRights: DATA_RIGHTS.relationship,
    label: "Served by company",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: COMPANY_OPTIONS,
    help: "Households with any relationship with this company. “Is any of” scopes to several companies at once.",
    wired: true,
    legacyKey: "company",
  },
  {
    key: "marketing_opt_out",
    dataRights: DATA_RIGHTS.identity,
    label: "Marketing opt-out",
    group: "Contact & consent",
    type: "enum",
    ops: ["eq", "in"],
    options: MARKETING_OPT_OUT_OPTIONS,
    help: "Who asked a company to stop its marketing email. Pick a company to find households where someone opted out of that company (the same record every send checks), “Any company” for any household with a member who opted out of at least one, or “All marketing” for households with someone who stopped all five. “Is any of” matches any of the picks. Read-only: this finds households, it never changes a preference.",
    computed: true,
    wired: true,
  },
  {
    key: "active_client_of",
    dataRights: DATA_RIGHTS.relationship,
    label: "Active client of",
    group: "Relationship",
    type: "enum",
    // See the contacts copy of this field — "is any of" is the OR across
    // companies; two conditions are the AND (2026-08-20); "is not" is the
    // cross-sell gap (2026-10-29).
    ops: ["eq", "neq", "in"],
    options: ANY_COMPANY_OPTIONS,
    help: "The household has at least one active service with that company. “Is any of” matches households active with ANY of the companies picked; add a second “Active client of” filter to require BOTH. “Is not” leaves out that company's active clients (pair it with another company to find a cross-sell gap). “Any company” means a current Stewardship household.",
    computed: true,
    wired: true,
  },
  {
    // 2026-08-20 — households could already be filtered by who they're an
    // active client of, but not by who they're a PROSPECT of, so
    // "households we're quoting for Insurance" had no filter to compile
    // to. Contacts have carried this field since the registry shipped.
    key: "prospect_of",
    dataRights: DATA_RIGHTS.relationship,
    label: "Prospect of",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: ANY_COMPANY_OPTIONS,
    help: "The household is currently in Opportunities with that company. “Is any of” matches any of the companies picked.",
    computed: true,
    wired: true,
  },
  {
    key: "advisor",
    dataRights: DATA_RIGHTS.relationship,
    label: "Current advisor",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "advisor",
    help: "The advisor currently responsible for the household in any company.",
    wired: true,
    legacyKey: "advisor",
  },
  {
    key: "fit",
    dataRights: DATA_RIGHTS.advisorJudgment,
    label: "Fit assessment",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: FIT_OPTIONS,
    help: "The household's fit assessment (best across companies). Internal-only.",
    wired: true,
  },
  {
    key: "wealth_stage",
    dataRights: DATA_RIGHTS.advisorJudgment,
    label: "Stage of Wealth",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: WEALTH_STAGE_OPTIONS,
    help: "Where this family is in Stewardship's three stages of wealth building: Control (getting the basics in place), Margin (room to breathe, starting to invest), Impact (real wealth and a plan for it). Read from this person's household, since the stage belongs to the family rather than the individual. Internal-only — never shown to households.",
    example: "is Margin",
    wired: true,
  },
  {
    key: "aum",
    dataRights: DATA_RIGHTS.financialTransaction,
    label: "AUM",
    group: "Financial",
    type: "number",
    ops: ["gte", "lte", "between"],
    unit: "dollars",
    help: "Assets under management — the sum of this household's custodian-confirmed accounts (Schwab, Betterment, Altruist), including managed retirement plans. Betterment cash never counts.",
    example: "over 500000",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "services_count",
    dataRights: DATA_RIGHTS.relationship,
    label: "Companies served",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte", "eq", "between"],
    unit: "count",
    help: "How many Stewardship companies actively serve this household.",
    computed: true,
    wired: true,
  },
  {
    key: "last_contact_days",
    dataRights: DATA_RIGHTS.relationship,
    label: "Days since last contact",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "days",
    help: "Days since the last real touch — calls, meetings, personal emails. Bulk sends never count.",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "last_inbound_days",
    dataRights: DATA_RIGHTS.relationship,
    label: "Days since they last engaged",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "days",
    help: "Days since the household last reached back — replied, called or texted in, or kept a meeting. Never-replied counts as maximally stale. Finds households going quiet on us.",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "last_outbound_days",
    dataRights: DATA_RIGHTS.relationship,
    label: "Days since we last reached out",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "days",
    help: "Days since we last contacted the household — our calls, meetings, and personal emails. Bulk sends never count. Finds households we owe a touch.",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "engagement_tier",
    dataRights: DATA_RIGHTS.computedScore,
    label: "Engagement",
    group: "Relationship",
    type: "enum",
    ops: ["eq", "in"],
    options: ENGAGEMENT_TIER_OPTIONS,
    help: "Overall relationship engagement — Thriving / Steady / Slipping / Disengaged. Combines whether we're keeping cadence with the household and whether they engage back.",
    computed: true,
    wired: true,
  },
  {
    key: "engagement_service_effort",
    dataRights: DATA_RIGHTS.computedScore,
    label: "Service effort (0-100)",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "score",
    help: "Are WE doing our job — outbound cadence vs. what this household's fit tier should get. Low = we owe them a touch.",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "engagement_responsiveness",
    dataRights: DATA_RIGHTS.computedScore,
    label: "Responsiveness (0-100)",
    group: "Relationship",
    type: "number",
    ops: ["gte", "lte"],
    unit: "score",
    help: "Is the CLIENT engaging back — replies, inbound contact, recent reviews. Low = going quiet on us.",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "client_since",
    dataRights: DATA_RIGHTS.relationship,
    label: "Client since",
    group: "Relationship",
    type: "date",
    ops: ["lte", "gte", "between", "more_than_days_ago", "within_last_days"],
    help: "When the household first became a client of any Stewardship company.",
    wired: true,
    sortable: true,
  },
  {
    key: "city",
    dataRights: DATA_RIGHTS.identity,
    label: "City",
    group: "Location",
    type: "string",
    ops: ["eq", "contains"],
    help: "The household's primary city.",
    example: "Gilbert",
    wired: true,
    sortable: true,
  },
  {
    key: "state",
    dataRights: DATA_RIGHTS.identity,
    label: "State",
    group: "Location",
    type: "string",
    ops: ["eq", "in"],
    help: "Two-letter state code, e.g. AZ.",
    wired: true,
  },
  {
    key: "zip",
    dataRights: DATA_RIGHTS.identity,
    label: "ZIP code",
    group: "Location",
    type: "string",
    ops: ["eq", "contains"],
    help: "Exact ZIP, or “contains” for a prefix.",
    wired: true,
  },
  {
    key: "member_count",
    dataRights: DATA_RIGHTS.identity,
    label: "People in household",
    group: "Household",
    type: "number",
    ops: ["eq", "gte", "lte"],
    unit: "count",
    help: "How many contacts belong to the household.",
    computed: true,
    wired: true,
  },
  // ── Insurance book composition (Grant, 2026-08-20 audit): "knowing
  // what households have and dont have … which ones have umbrella or
  // dont … so we can do searches in ask stew for all of this."
  // Definitions match shared/db/insurance-book.ts exactly (active,
  // non-deleted service records; "home" = the shelter line: home/condo/
  // renters), so a filter list and the dashboard tiles can never
  // disagree. Booleans compose: home Yes + auto No is the mono-line
  // cross-sell list; home Yes + auto Yes + umbrella No is the umbrella
  // gap list.
  {
    key: "has_home_policy",
    dataRights: DATA_RIGHTS.insuranceTransaction,
    label: "Has home policy",
    group: "Insurance",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "The household has an active home, condo, or renters policy with Stewardship Insurance. “No” finds households missing the shelter line.",
    computed: true,
    wired: true,
  },
  {
    key: "has_auto_policy",
    dataRights: DATA_RIGHTS.insuranceTransaction,
    label: "Has auto policy",
    group: "Insurance",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "The household has an active auto policy with Stewardship Insurance. Combine with “Has home policy” for mono-line and full-household lists.",
    computed: true,
    wired: true,
  },
  {
    key: "has_umbrella_policy",
    dataRights: DATA_RIGHTS.insuranceTransaction,
    label: "Has umbrella policy",
    group: "Insurance",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "The household has an active umbrella policy. Home Yes + auto Yes + umbrella No is the umbrella cross-sell list.",
    computed: true,
    wired: true,
  },
  {
    key: "insurance_premium",
    dataRights: DATA_RIGHTS.insuranceTransaction,
    label: "Insurance premium (annual)",
    group: "Insurance",
    type: "number",
    ops: ["between", "gte", "lte"],
    unit: "dollars",
    help: "The household's total annual premium across its active Stewardship Insurance policies, straight from the carrier feed.",
    example: "at least 5000",
    computed: true,
    wired: true,
  },
  {
    key: "insurance_claims",
    dataRights: DATA_RIGHTS.insuranceTransaction,
    label: "Carrier claims recorded",
    group: "Insurance",
    type: "number",
    ops: ["gte", "eq", "lte"],
    unit: "count",
    help: "How many claims carriers have reported for this household (IVANS feed, 2026 onward — earlier claims were never transmitted).",
    example: "at least 1",
    computed: true,
    wired: true,
  },
  {
    key: "archived",
    dataRights: DATA_RIGHTS.identity,
    label: "Archived",
    group: "Record",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "“Yes” shows only archived households.",
    wired: true,
    legacyKey: "archived",
  },
];

// Opportunities and Processing share most of their vocabulary; the
// builder keeps them as separate lists because stages, examples and a
// few fields (blocked / pace) differ.
function workflowCommonFields(kind: "opportunities" | "processing"): FilterField[] {
  const noun = kind === "opportunities" ? "opportunity" : "processing record";
  return [
    {
      key: "company",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Company",
      group: "Workflow",
      type: "enum",
      ops: ["eq", "in"],
      options: COMPANY_OPTIONS,
      help: `Which company's ${noun}s to show. “Is any of” scopes to several companies at once.`,
      wired: true,
      legacyKey: "company",
    },
    {
      key: "stage",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Stage",
      group: "Workflow",
      type: "enum",
      ops: ["eq", "in"],
      optionsSource: "stage",
      help: "The current stage. Stage names come from each company's stage templates.",
      wired: true,
      legacyKey: "stage",
    },
    {
      // The server has honored `type` since the boards were built (it is
      // how the Processing board focuses one pipeline) — but it was never
      // declared here, so neither the "+ Filter" builder nor Ask Stew
      // could offer it, and Andrea had no way to see "only Estate Plan"
      // or "only Medicare" opportunities (2026-08-30). Declaring it is
      // the whole fix; the SQL wiring already existed.
      key: "type",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Product line",
      group: "Workflow",
      type: "enum",
      ops: ["eq", "in"],
      optionsSource: "workflow_type",
      help:
        kind === "opportunities"
          ? "The kind of opportunity — Health Coverage, Estate Plan Consultation, Home Policy quote, … Narrows the list or board to one product line."
          : "The kind of processing record — Health & Medicare Case, Estate Plan Case, Mortgage Loan, … Narrows the list or board to one product line.",
      wired: true,
      legacyKey: "type",
    },
    {
      key: "owner",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Owner",
      group: "Workflow",
      type: "enum",
      ops: ["eq", "in"],
      optionsSource: "advisor",
      help: `The advisor who owns the ${noun}.`,
      wired: true,
      legacyKey: "owner",
    },
    {
      key: "outcome",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Outcome",
      group: "Workflow",
      type: "enum",
      ops: ["eq"],
      options:
        kind === "opportunities"
          ? OPPORTUNITY_OUTCOME_OPTIONS
          : PROCESSING_OUTCOME_OPTIONS,
      help:
        kind === "opportunities"
          ? "Open (still in motion), won, lost, or abandoned. Lists show open by default."
          : "Open (still in motion), completed, fell through, or cancelled. Lists show open by default.",
      wired: true,
      legacyKey: "outcome",
    },
    {
      key: "fit",
      dataRights: DATA_RIGHTS.recordJudgment,
      label: "Fit assessment",
      group: "Household",
      type: "enum",
      ops: ["eq"],
      options: FIT_OPTIONS,
      help: "The household's fit assessment with this company. Internal-only.",
      wired: true,
      legacyKey: "fit",
    },
    {
      key: "value",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Estimated revenue (Year 1)",
      group: "Value",
      type: "number",
      ops: ["between", "gte", "lte"],
      unit: "dollars",
      help: "Estimated Year-1 revenue to Stewardship — the dollars field of the three-field value model.",
      example: "at least 5000",
      wired: true,
      legacyKey: "value",
      sortable: true,
    },
    {
      key: "days_idle",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Days without an update",
      group: "Pace",
      type: "number",
      ops: ["gte"],
      unit: "days",
      help: "Days since anything changed on the record. High numbers usually mean stalled work.",
      example: "at least 30",
      computed: true,
      wired: true,
      legacyKey: "stalled_days",
    },
    {
      key: "expected_close_date",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Expected close date",
      group: "Dates",
      type: "date",
      ops: ["next_within_days", "lte", "gte", "between"],
      help: "When the owner expects this to close.",
      example: "in the next 30 days",
      wired: true,
      sortable: true,
    },
    {
      key: "days_open",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Days open",
      group: "Pace",
      type: "number",
      ops: ["gte", "lte"],
      unit: "days",
      help: "Days since the record was opened.",
      computed: true,
      wired: true,
      sortable: true,
    },
    {
      // Ask Stew Wave 3a (2026-09-18). The stage clock (migration 0206)
      // has driven the list's "days in stage" column and the stalled
      // chip for months, but was never a filter, so "loans stuck in
      // underwriting more than 30 days" could be seen and not asked for.
      // Distinct from days_idle (last edit) and days_open (record age).
      key: "days_in_stage",
      dataRights: DATA_RIGHTS.recordTransaction,
      label: "Days in current stage",
      group: "Pace",
      type: "number",
      ops: ["gte", "lte", "between"],
      unit: "days",
      help: "Days since the record entered its current stage — the stage clock, not the last edit. Long numbers usually mean stuck work.",
      example: "at least 30",
      computed: true,
      wired: true,
      sortable: true,
    },
    {
      key: "product_metric",
      dataRights: DATA_RIGHTS.recordApplication,
      label: "Product metric",
      group: "Value",
      type: "number",
      ops: ["between", "gte", "lte"],
      help: `The industry-native number the advisor knows for this ${noun}: origination amount, premium, AUM, sale price. Units differ by company; the Year-1 revenue field is the comparable dollar figure.`,
      example: "at least 250000",
      wired: true,
      sortable: true,
    },
  ];
}

const OPPORTUNITY_FIELDS: FilterField[] = [
  ...workflowCommonFields("opportunities"),
  {
    // Per-opportunity origination (Phase 1, 2026-07-18). Distinct from the
    // household's contact source: a household's 2nd/3rd opportunity (e.g. an
    // Insurance cross-sell after a Mortgage) records how THAT deal came about
    // — most importantly internal cross-sells. Opportunity-only; Processing
    // has no source column.
    key: "source_type",
    dataRights: DATA_RIGHTS.originRecorded,
    label: "Source",
    group: "Workflow",
    type: "enum",
    ops: ["eq", "in"],
    options: REFERRAL_SOURCE_FILTER_OPTIONS,
    help: "Where this opportunity originated — including cross-company internal referrals.",
    wired: true,
  },
];

const PROCESSING_FIELDS: FilterField[] = [
  ...workflowCommonFields("processing"),
  {
    key: "blocked",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Blocked",
    group: "Pace",
    type: "boolean",
    ops: ["eq"],
    options: BOOLEAN_OPTIONS,
    help: "Something on the record is marked as blocking progress.",
    wired: true,
    legacyKey: "blocked",
  },
  // past_pace / closing_soon are YES-ONLY (audit fix 2026-06-10):
  // their predicates run in the JS pace path, which has no "No"
  // branch — offering "No" produced a chip that filtered nothing.
  {
    key: "past_pace",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Behind typical pace",
    group: "Pace",
    type: "boolean",
    ops: ["eq"],
    options: [{ value: "true", label: "Yes" }],
    help: "Slower than the typical number of days for its current stage (computed from stage templates).",
    computed: true,
    wired: true,
    legacyKey: "past_pace",
  },
  {
    key: "closing_soon",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Closing soon",
    group: "Pace",
    type: "boolean",
    ops: ["eq"],
    options: [{ value: "true", label: "Yes" }],
    help: "On pace and expected to close within the closing-soon window.",
    computed: true,
    wired: true,
    legacyKey: "closing_soon",
  },
];

const ONGOING_CARE_FIELDS: FilterField[] = [
  {
    key: "company",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Company",
    group: "Plan",
    type: "enum",
    ops: ["eq", "in"],
    options: COMPANY_OPTIONS,
    help: "Which company's care plans to show. “Is any of” scopes to several companies at once.",
    wired: true,
    legacyKey: "company",
  },
  {
    key: "plan_type",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Plan",
    group: "Plan",
    type: "enum",
    // "in" since 2026-09-28: one name can mean several templates (two are
    // both called "Medicare Renewal Plan"), and Ask Stew's list link must
    // carry both or the screen drops the plan filter and shows every plan.
    ops: ["eq", "in"],
    optionsSource: "rhythm_template",
    help: "The plan template — Renewal Plan, Annual Review Plan, Rate Watch, Birthday Plan, …",
    wired: true,
    legacyKey: "type",
  },
  {
    // Compiled since Grant's 2026-07-22 ask ("just Medicare" vs Health/
    // Dental inside one plan) but never registered — so neither the
    // builder UI nor Ask Stew could offer it. 2026-08-20 audit fix.
    key: "product",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Product",
    group: "Plan",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "service_type",
    help: "The linked service's product — Home Policy, Medicare Case, … Narrows a plan list to one product line.",
    wired: true,
  },
  {
    key: "status",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Status",
    group: "Plan",
    type: "enum",
    ops: ["eq"],
    options: RHYTHM_STATUS_OPTIONS,
    help: "Whether the plan cycle is running, paused, completed, or ended.",
    wired: true,
    legacyKey: "status",
  },
  {
    // Workflow stage on the renewal boards. Filterable via the legacy
    // `stage` key since 2026-08-20 (Grant's list/board parity rule) but
    // never REGISTERED — so the builder couldn't offer it, Ask Stew
    // couldn't use it, and no link could ask for more than one stage at
    // a time. Registering it adds "is any of", which is what a work
    // queue spanning two stages (Andrea's "info received" lane, which
    // covers Info Received AND Info Received - Need to Quote) needs to
    // link to exactly the records it counted. Same class of gap as the
    // `product` field fixed in the 2026-08-20 audit, two entries up.
    key: "stage",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Stage",
    group: "Plan",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "stage",
    help: "Where the cycle sits on its renewal board — Info Received, Quoted, Enrolled, …",
    wired: true,
    legacyKey: "stage",
  },
  {
    key: "owner",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Owner",
    group: "Plan",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "advisor",
    help: "The advisor responsible for this plan.",
    wired: true,
    legacyKey: "owner",
  },
  {
    key: "anchor_date",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Renews (anchor date)",
    group: "Dates",
    type: "date",
    ops: ["next_within_days", "between", "lte", "gte"],
    help: "When this plan's anchor next lands — for a Renewal Plan that IS the policy's renewal date. “In the next 60 days” is the renewal-window question.",
    example: "in the next 60 days",
    wired: true,
    sortable: true,
  },
  {
    key: "cycle_ends",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Cycle ends",
    group: "Dates",
    type: "date",
    ops: ["next_within_days", "lte", "gte", "between"],
    help: "When the current 12-month TRACKING cycle ends — roughly a year after the anchor. For the renewal date itself, use “Renews (anchor date)”.",
    wired: true,
    sortable: true,
  },
  // Joy feedback PR 5 — the linked insurance policy's premiums, so
  // "renewals quoted above current" style questions are one condition.
  // Plans with no linked policy have no value and never match.
  {
    key: "current_premium",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Current premium",
    group: "Policy",
    type: "number",
    ops: ["between", "gte", "lte", "eq"],
    unit: "dollars",
    help: "The linked policy's current annual premium. Only present on plans linked to an insurance policy.",
    example: "at least 2000",
    wired: true,
    sortable: true,
  },
  {
    key: "renewal_premium_offer",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Renewal offer",
    group: "Policy",
    type: "number",
    ops: ["between", "gte", "lte", "eq"],
    unit: "dollars",
    help: "The carrier's quoted premium for the upcoming renewal, captured while reviewing the quote.",
    example: "at least 2500",
    wired: true,
    sortable: true,
  },
];

const SERVICE_RECORD_FIELDS: FilterField[] = [
  {
    key: "company",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Company",
    group: "Service",
    type: "enum",
    ops: ["eq", "in"],
    options: COMPANY_OPTIONS,
    help: "Which company the service belongs to. “Is any of” scopes to several companies at once.",
    wired: true,
    legacyKey: "company",
  },
  {
    key: "status",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Status",
    group: "Service",
    type: "enum",
    ops: ["eq", "in"],
    options: SERVICE_STATUS_OPTIONS,
    help: "Lifecycle state of the service record.",
    wired: true,
    legacyKey: "status",
  },
  {
    key: "family",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Service family",
    group: "Service",
    type: "enum",
    ops: ["eq"],
    optionsSource: "service_family",
    help: "The broad product category — Policy, Loan, Portfolio, Case, Listing.",
    wired: true,
    legacyKey: "family",
  },
  {
    key: "type",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Service type",
    group: "Service",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "service_type",
    help: "The specific product — Home Policy, Refinance Mortgage, Managed Portfolio, …",
    wired: true,
    legacyKey: "type",
  },
  {
    key: "advisor",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Current advisor",
    group: "Service",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "advisor",
    help: "The advisor currently responsible for this service.",
    wired: true,
    legacyKey: "advisor",
  },
  {
    key: "carrier",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Carrier",
    group: "Service",
    type: "enum",
    ops: ["eq", "in"],
    optionsSource: "carrier",
    help: "The carrier behind the policy (insurance/planning services).",
    wired: true,
    legacyKey: "carrier",
  },
  {
    key: "effective_date",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Effective date",
    group: "Dates",
    type: "date",
    ops: ["between", "lte", "gte", "within_last_days"],
    help: "Policy inception, loan funding, contract signing — when the service took effect.",
    wired: true,
    sortable: true,
  },
  {
    // 2026-09-22 (Nate): "how many Realty transactions closed 10 to 13
    // months ago?" had no field to run on — the close date was on every
    // closed Realty record (migration 0195) and in no filter, so Ask Stew
    // answered 0. Loans never got service_records.closed_date; a loan's
    // close IS its funding, and that fact's home is mortgage_loans.
    // funded_date, read live here rather than copied (one home per fact).
    // Without the fallback this filter would quietly answer 0 for every
    // Mortgage question — the same wrong number, one company over.
    key: "closed_date",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Closed / funded date",
    group: "Dates",
    type: "date",
    ops: ["between", "lte", "gte", "within_last_days"],
    help: "The day it closed: a home sale's closing, a loan's funding. Empty for records still in motion and for policies, which end instead of closing.",
    example: "is between Aug 1 and Oct 31",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    // Derived loan outcome (Grant's ruling 2026-07-11): a past loan
    // that funded is different from one that fell through, and
    // service_records.status can't say which (no 'cancelled' value).
    // Computed from mortgage_loans dates — see shared/db/loan-disposition.ts.
    key: "loan_disposition",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Loan disposition",
    group: "Service",
    type: "enum",
    ops: ["eq", "in"],
    options: [
      { value: "closed", label: "Closed (funded)" },
      { value: "cancelled", label: "Cancelled / withdrawn" },
      { value: "open", label: "Open" },
    ],
    help: "How the loan ended — Closed (it funded) vs Cancelled / withdrawn (it fell through), or Open while still in motion. Derived from the loan's dates; only matches loan-family services.",
    example: "is Cancelled / withdrawn",
    computed: true,
    wired: true,
    legacyKey: "loan_disposition",
  },
  {
    // What the county recorder says about the house behind a traditional
    // loan (win-back, Grant 2026-09-19/22): still ours, refinanced with
    // another lender, sold, unclear, or not checked yet. Category 1: read
    // live from the county record, never typed. Private Money is exempt
    // and never matches. Computed by countyStandingSql() in
    // shared/db/county-record.ts, the same rule the household page uses.
    key: "county_standing",
    dataRights: DATA_RIGHTS.mortgagePublicRecord,
    label: "County standing",
    group: "Service",
    type: "enum",
    ops: ["eq", "in"],
    options: [
      { value: "refinanced_away", label: "Refinanced away" },
      { value: "sold", label: "Sold" },
      { value: "still_ours", label: "Still ours" },
      { value: "unclear", label: "Unclear" },
      { value: "none", label: "Not checked yet" },
    ],
    help: "What the county record says about the house behind a traditional loan: still ours, refinanced with another lender, or sold. Not checked yet means the nightly county pull hasn't reached that house. Only matches traditional loans; Private Money is left out.",
    example: "is Refinanced away",
    computed: true,
    wired: true,
    sortable: true,
  },
  // ── Insurance policy detail (Grant, 2026-08-20 audit): premium
  // ranges, renewal windows, and claims — filterable so Ask Stew and
  // list exports answer them without a dashboard. Values come from the
  // linked insurance policy row the IVANS feed keeps current; services
  // with no insurance policy have no value and never match.
  {
    key: "annual_premium",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Annual premium",
    group: "Policy",
    type: "number",
    ops: ["between", "gte", "lte", "eq"],
    unit: "dollars",
    help: "The policy's current annual premium, straight from the carrier feed. Insurance policies only.",
    example: "at least 2000",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "expiration_date",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Renews / expires",
    group: "Dates",
    type: "date",
    ops: ["next_within_days", "between", "lte", "gte"],
    help: "The policy's expiration date — its renewal date. “In the next 60 days” is the renewal-window question. Insurance policies only.",
    example: "in the next 60 days",
    computed: true,
    wired: true,
    sortable: true,
  },
  {
    key: "claims_count",
    dataRights: DATA_RIGHTS.recordTransaction,
    label: "Carrier claims recorded",
    group: "Policy",
    type: "number",
    ops: ["gte", "eq", "lte"],
    unit: "count",
    help: "How many claims carriers have reported against this policy (IVANS feed, 2026 onward — earlier claims were never transmitted).",
    example: "at least 1",
    computed: true,
    wired: true,
  },
];

export const ENTITY_FILTER_FIELDS: Record<FilterEntity, readonly FilterField[]> = {
  contacts: CONTACT_FIELDS,
  households: HOUSEHOLD_FIELDS,
  opportunities: OPPORTUNITY_FIELDS,
  processing: PROCESSING_FIELDS,
  "ongoing-care": ONGOING_CARE_FIELDS,
  "service-records": SERVICE_RECORD_FIELDS,
};

// ─── Lookup helpers ──────────────────────────────────────────────────

function buildFieldMaps(): Record<FilterEntity, ReadonlyMap<string, FilterField>> {
  const out = {} as Record<FilterEntity, ReadonlyMap<string, FilterField>>;
  for (const e of FILTER_ENTITIES) {
    out[e] = new Map(ENTITY_FILTER_FIELDS[e].map((f) => [f.key, f]));
  }
  return out;
}

const FIELD_MAPS = buildFieldMaps();

export function fieldByKey(
  entity: FilterEntity,
  key: string,
): FilterField | null {
  return FIELD_MAPS[entity].get(key) ?? null;
}

export function wiredFields(entity: FilterEntity): FilterField[] {
  return ENTITY_FILTER_FIELDS[entity].filter((f) => f.wired);
}

export function isFilterEntity(s: unknown): s is FilterEntity {
  return (
    typeof s === "string" && (FILTER_ENTITIES as readonly string[]).includes(s)
  );
}

// ─── Legacy wire-key mapping ─────────────────────────────────────────
//
// Every `f=key:value` filter the OS has ever written (quick-filter
// chips, hardcoded saved views, bookmarked URLs, saved_views rows from
// PR #759) must parse into the new model forever. Each entry converts
// one legacy key+value into a FilterCondition, or null when the value
// was malformed (matching the server's silent-drop behavior).

type LegacyConverter = (value: string) => FilterCondition | null;

function eqOf(field: string): LegacyConverter {
  return (value) => ({ field, op: "eq", values: [value] });
}

/** Legacy numeric range "min-max" / "min-" / "-max" / "n". */
function rangeOf(field: string): LegacyConverter {
  return (value) => {
    if (!value) return null;
    const parts = value.split("-");
    const first = parts[0] ?? "";
    if (parts.length === 1) {
      const n = Number(first);
      return Number.isFinite(n) ? { field, op: "eq", values: [first] } : null;
    }
    const second = parts[1] ?? "";
    const min = first === "" ? null : Number(first);
    const max = second === "" ? null : Number(second);
    const minOk = min !== null && Number.isFinite(min);
    const maxOk = max !== null && Number.isFinite(max);
    if (minOk && maxOk) {
      return { field, op: "between", values: [first, second] };
    }
    if (minOk) return { field, op: "gte", values: [first] };
    if (maxOk) return { field, op: "lte", values: [second] };
    return null;
  };
}

function boolTrueOf(field: string): LegacyConverter {
  // Legacy boolean keys were presence-style: only "true"/"1" flipped
  // the behavior; anything else fell back to the default (false).
  return (value) => ({
    field,
    op: "eq",
    values: [value === "true" || value === "1" ? "true" : "false"],
  });
}

function gteIntOf(field: string): LegacyConverter {
  return (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return { field, op: "gte", values: [String(Math.floor(n))] };
  };
}

export const LEGACY_FILTER_MAP: Record<
  FilterEntity,
  Record<string, LegacyConverter>
> = {
  contacts: {
    company: eqOf("company"),
    fit: eqOf("fit"),
    city: eqOf("city"),
    archived: boolTrueOf("archived"),
    services_count_min: gteIntOf("services_count"),
    birthday_month: eqOf("birthday_month"),
  },
  households: {
    company: eqOf("company"),
    advisor: eqOf("advisor"),
    archived: boolTrueOf("archived"),
  },
  opportunities: {
    company: eqOf("company"),
    stage: eqOf("stage"),
    // A board writes its whole covered-type set pipe-joined
    // (f=type:a|b — see pushBoardTypeFilter); a single product line is a
    // plain eq.
    type: (v) =>
      v.includes("|")
        ? {
            field: "type",
            op: "in",
            values: v.split("|").map((t) => t.trim()).filter(Boolean),
          }
        : { field: "type", op: "eq", values: [v] },
    owner: eqOf("owner"),
    outcome: eqOf("outcome"),
    fit: eqOf("fit"),
    value: rangeOf("value"),
    stalled_days: gteIntOf("days_idle"),
    closes_before: (v) => ({
      field: "expected_close_date",
      op: "lte",
      values: [v],
    }),
    closes_after: (v) => ({
      field: "expected_close_date",
      op: "gte",
      values: [v],
    }),
  },
  processing: {
    company: eqOf("company"),
    // A board writes its whole covered-type set pipe-joined
    // (f=type:a|b — see pushBoardTypeFilter); a single product line is a
    // plain eq.
    type: (v) =>
      v.includes("|")
        ? {
            field: "type",
            op: "in",
            values: v.split("|").map((t) => t.trim()).filter(Boolean),
          }
        : { field: "type", op: "eq", values: [v] },
    stage: eqOf("stage"),
    owner: eqOf("owner"),
    outcome: eqOf("outcome"),
    fit: eqOf("fit"),
    value: rangeOf("value"),
    stalled_days: gteIntOf("days_idle"),
    closes_before: (v) => ({
      field: "expected_close_date",
      op: "lte",
      values: [v],
    }),
    closes_after: (v) => ({
      field: "expected_close_date",
      op: "gte",
      values: [v],
    }),
    blocked: boolTrueOf("blocked"),
    past_pace: boolTrueOf("past_pace"),
    closing_soon: boolTrueOf("closing_soon"),
  },
  "ongoing-care": {
    company: eqOf("company"),
    type: eqOf("plan_type"),
    status: eqOf("status"),
    owner: eqOf("owner"),
  },
  "service-records": {
    company: eqOf("company"),
    status: eqOf("status"),
    family: eqOf("family"),
    type: eqOf("type"),
    advisor: eqOf("advisor"),
    carrier: eqOf("carrier"),
    loan_disposition: eqOf("loan_disposition"),
  },
};

/**
 * Convert one legacy `key:value` filter into the new model. Returns
 * null for unknown keys or unconvertible values — callers keep those
 * as opaque passthrough so nothing a URL carries is ever dropped.
 */
export function legacyToCondition(
  entity: FilterEntity,
  key: string,
  value: string,
): FilterCondition | null {
  const convert = LEGACY_FILTER_MAP[entity][key];
  if (!convert) return null;
  return convert(value);
}
